import React from "react";
import NavigationHeader from "@/components/NavigationHeader";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import HomeAuroraBackground from "@/components/HomeAuroraBackground";
import dpoDpsMark from "@/assets/dpo-dps-mark.webp";
import { motion } from "framer-motion";
import {
  Shield,
  Lock,
  Database,
  UserCheck,
  AlertCircle,
  Mail,
  FileText,
  Share2,
  Globe,
  RefreshCw,
  Check,
  ShieldCheck,
} from "lucide-react";

const Privacy = () => {
  const sections = [
    {
      icon: FileText,
      title: "1. Information We Collect",
      content:
        "We collect information necessary to operate the platform effectively. This includes:",
      list: [
        "Clinic and staff details",
        "Client and pet records",
        "Medical history and SOAP notes",
        "Appointment, billing, and usage logs",
      ],
    },
    {
      icon: Database,
      title: "2. How We Use Information",
      content: "Data is processed strictly to:",
      list: [
        "Deliver Vet Assist services",
        "Improve system performance",
        "Provide technical support",
        "Comply with legal obligations",
      ],
    },
    {
      icon: Lock,
      title: "3. Data Storage & Security",
      list: [
        "Data is stored securely with industry-standard encryption",
        "Strict access controls are enforced",
        "Regular automated backups protect against data loss",
      ],
    },
    {
      icon: Shield,
      title: "4. Data Retention Policy",
      list: [
        "Active accounts retain full data access",
        "Inactive or unpaid accounts may be archived",
        "Deleted data follows a strict retention window before permanent erasure",
      ],
    },
    {
      icon: Share2,
      title: "5. Data Sharing",
      content: "Data is never sold to third parties. Information is only shared:",
      list: [
        "With authorized service partners (strictly for operational needs)",
        "When legally required by law or court order",
      ],
    },
    {
      icon: UserCheck,
      title: "6. Client Rights",
      content: "As a client, you maintain the right to:",
      list: [
        "Access your data at any time",
        "Request full data exports",
        "Request correction or deletion (subject to legal compliance)",
      ],
    },
    {
      icon: Globe,
      title: "7. Third-Party Services",
      content:
        "Vet Assist may integrate with third-party tools (e.g., SMS gateways). These providers operate under their own independent privacy policies.",
    },
    {
      icon: AlertCircle,
      title: "8. Data Breach Notification",
      content:
        "In the unlikely event of a data breach, affected clients will be notified promptly and transparently in accordance with applicable laws.",
    },
    {
      icon: RefreshCw,
      title: "9. Policy Updates",
      content:
        "This Privacy Policy may be updated periodically. Continued use of the platform constitutes acceptance of any changes.",
    },
    {
      icon: Mail,
      title: "10. Contact Information",
      content:
        "If you have questions regarding this policy, please contact our Data Protection Officer:",
      list: ["Email: customercare@vetassist.app", "Phone/Viber: 02-7275-0125 / 0917-145-9294"],
    },
  ];

  return (
    <>
      <SEO page="privacy" />
      <div
        className="relative w-full font-sans text-slate-900 selection:bg-green-500/30 selection:text-green-900"
        style={{
          background: "linear-gradient(135deg, #dbeafe 0%, #e8f4ff 20%, #f0fdf4 60%, #dcfce7 100%)",
        }}>
        <HomeAuroraBackground
          showStars={false}
          showWavingLines={true}
          className="pointer-events-none fixed inset-0 -z-10 overflow-hidden"
        />

        <NavigationHeader />

        {/* Hero Section */}
        <section className="relative px-6 pt-40 pb-16">
          <div className="mx-auto max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-8 inline-flex items-center gap-2 rounded-full border border-green-500/20 bg-green-500/5 px-4 py-1.5 shadow-sm backdrop-blur-sm">
              <ShieldCheck className="h-4 w-4 text-green-400" />
              <span className="text-xs font-semibold tracking-wide text-green-800 uppercase">
                Data Protection & Security
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mb-6 text-5xl leading-[1.1] font-extrabold tracking-tight text-[#1A3A5C] md:text-7xl">
              Privacy{" "}
              <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                Policy
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mx-auto max-w-2xl text-xl leading-relaxed text-slate-700">
              Your trust is our priority. Here is how we handle, store, and protect your data.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.28 }}
              className="mt-8 flex justify-center">
              <div className="rounded-2xl border border-green-100/70 bg-white/80 p-4 shadow-sm backdrop-blur-sm">
                <img
                  src={dpoDpsMark}
                  alt="Data privacy and protection mark"
                  className="h-auto w-40 object-contain sm:w-48"
                  loading="lazy"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-6 text-sm font-medium text-slate-600">
              Last updated: February 2026
            </motion.div>
          </div>
        </section>

        {/* Content Section */}
        <div className="mx-auto max-w-5xl px-4 pb-12 md:px-8">
          <div className="grid gap-6">
            {sections.map((section, index) => {
              const Icon = section.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="group relative rounded-2xl border border-gray-100 bg-white/70 p-8 shadow-sm backdrop-blur-md transition-all duration-300 hover:border-green-200 hover:shadow-xl hover:shadow-green-500/5">
                  <div className="flex flex-col gap-6 md:flex-row">
                    {/* Icon Container */}
                    <div className="flex-shrink-0">
                      <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-green-50 bg-gradient-to-br from-green-50 to-white text-green-600 shadow-sm transition-all duration-300 group-hover:scale-105 group-hover:bg-green-600 group-hover:bg-none group-hover:text-white">
                        <Icon className="h-7 w-7" strokeWidth={1.5} />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 pt-1">
                      <h2 className="mb-4 flex items-center gap-2 text-xl font-bold text-gray-900 transition-colors group-hover:text-green-700 md:text-2xl">
                        {section.title}
                      </h2>

                      {section.content && (
                        <p className="mb-6 leading-relaxed text-gray-600">{section.content}</p>
                      )}

                      {section.list && (
                        <ul className="grid gap-3">
                          {section.list.map((item, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <div className="mt-1.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                                <Check className="h-3 w-3 text-green-600" />
                              </div>
                              <span className="leading-relaxed text-gray-600">{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default Privacy;
